import { toAppError, type AppError } from '@kdk/shared';
import type { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

export type AsyncState<T> =
  | { status: 'pending' }
  | { status: 'error'; error: AppError; retry: () => void }
  | { status: 'success'; data: T };

export const combine2 = <A, B, R>(
  a: AsyncState<A>,
  b: AsyncState<B>,
  combine: (a: A, b: B) => R,
): AsyncState<R> => {
  if (a.status === 'error') return a;
  if (b.status === 'error') return b;
  if (a.status === 'pending' || b.status === 'pending') return { status: 'pending' };
  return { status: 'success', data: combine(a.data, b.data) };
};

/** react-query の結果を AsyncState に変換する。同じ結果なら同じ参照を返す(useMemo の依存に使える)。 */
export const useAsyncState = <T>(query: UseQueryResult<T>): AsyncState<T> => {
  const { status, data, error, refetch } = query;
  return useMemo((): AsyncState<T> => {
    if (status === 'success') return { status: 'success', data };
    if (status === 'error') {
      return {
        status: 'error',
        error: toAppError(error),
        retry: () => {
          void refetch();
        },
      };
    }
    return { status: 'pending' };
  }, [status, data, error, refetch]);
};
